import { ChangeDetectionStrategy, Component } from '@angular/core'

import { formatPesos } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { StatementRow, StatementsBlock } from '@data/interfaces/risk-scorecard.interface'
import { RiskScorecardTabBase } from '../risk-scorecard-tab.base'
import { flagLabel } from '../risk-scorecard.labels'

/** Un renglón de la tabla ya aplanado: su nivel de sangría y si abre el resto de su rama. */
export interface StatementLine {
  row: StatementRow
  depth: number
  expandable: boolean
  expanded: boolean
}

/**
 * Estados financieros (plan v4.2 §8b.3): Estado de resultados y Balance general de la declaración anual del SAT, como
 * la hoja «EEFF Anual» del Excel. Hasta 3 ejercicios con % vertical y variación; se ven el primer y el segundo nivel y
 * el resto de cada rama se abre a pedido. Los números son los mismos que usa la calculadora.
 */
@Component({
  selector: 'app-ciec-statements-tab',
  templateUrl: './ciec-statements-tab.component.html',
  styleUrls: ['./ciec-statements-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CiecStatementsTabComponent extends RiskScorecardTabBase {
  statements: StatementsBlock | null = null
  income: StatementLine[] = []
  balance: StatementLine[] = []

  readonly pesos = formatPesos
  readonly flagLabel = flagLabel
  //* Ramas abiertas por llave de renglón: sobreviven a un recálculo de la respuesta.
  private readonly open = new Set<string>()

  get isPersonaFisica(): boolean {
    return this.result?.personType === 'PF'
  }

  /** Años después del primero: los que tienen variación contra el anterior. */
  hasDelta(index: number): boolean {
    return index > 0
  }

  percent(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—'
    return `${value < 0 ? '−' : ''}${Math.abs(value * 100).toFixed(1)} %`
  }

  delta(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—'
    return `${value > 0 ? '+' : ''}${this.percent(value)}`
  }

  /** Variación de un renglón contra el ejercicio anterior. Si el monto cambió de signo el porcentaje no dice nada (−$0.6M → $34M da +5363 %). */
  rowDelta(row: StatementRow, index: number): string {
    const before = row.values[this.statements?.years[index - 1] ?? '']
    const now = row.values[this.statements?.years[index] ?? '']
    if (before && now && Math.sign(before) !== Math.sign(now)) return 'cambia de signo'
    return this.delta(row.yoyPct[this.statements?.years[index] ?? ''])
  }

  amount(value: number | null | undefined): string {
    return value === null || value === undefined ? '—' : this.pesos(value)
  }

  /** El check de un ejercicio, si el motor lo calculó. */
  checkOf(year: string): StatementsBlock['check'][string] | null {
    return this.statements?.check[year] ?? null
  }

  toggle(line: StatementLine): void {
    if (this.open.has(line.row.key)) this.open.delete(line.row.key)
    else this.open.add(line.row.key)
    this.flatten()
  }

  protected override onResult(): void {
    this.statements = this.result?.statements ?? null
    this.flatten()
  }

  private flatten(): void {
    this.income = this.lines(this.statements?.income ?? [])
    this.balance = this.lines(this.statements?.balance ?? [])
    this.cd.markForCheck()
  }

  //* Primer y segundo nivel siempre; lo de abajo de un renglón de segundo nivel, solo con su rama abierta.
  private lines(rows: StatementRow[]): StatementLine[] {
    const out: StatementLine[] = []
    for (const top of rows) {
      out.push({ row: top, depth: 0, expandable: false, expanded: false })
      for (const child of visibleChildren(top)) {
        const expandable = !!visibleChildren(child).length
        const expanded = expandable && this.open.has(child.key)
        out.push({ row: child, depth: 1, expandable, expanded })
        if (expanded) for (const row of descendants(child)) out.push({ row, depth: 2, expandable: false, expanded: false })
      }
    }
    return out
  }
}

function descendants(row: StatementRow): StatementRow[] {
  return visibleChildren(row).flatMap((child) => [child, ...descendants(child)])
}

//* La declaración repite el título como único concepto («Utilidad Bruta» › «Utilidad Bruta»): ese hijo no aporta nada.
function visibleChildren(row: StatementRow): StatementRow[] {
  const children = row.children ?? []
  const [only] = children
  const echo = children.length === 1 && !only.children?.length && only.label.trim().toLowerCase() === row.label.trim().toLowerCase()
  return echo ? [] : children
}
